import type { OrpcClient } from '@/hooks/useClient'

type RescheduleResult = Awaited<ReturnType<OrpcClient['zocdocRescheduleAppointment']>>

/**
 * The outcome line for a completed reschedule, so the card renders one sentence rather than five.
 *
 * Its own module rather than a second export from `RescheduleSteps`: a file that exports both a
 * component and a helper loses React Fast Refresh for the component.
 *
 * The last branch is the one that matters. Zocdoc moved and beHuman did not is not a failure to
 * report quietly — the practitioner's calendar is now wrong, and only a person can fix it.
 *
 * A failed *confirm* is a milder thing and reads that way: both calendars hold the new time and
 * Zocdoc calls `rescheduled` settled, so nothing is broken — the status is just short of
 * `confirmed`. Said out loud all the same, because nothing else will say it.
 */
export function rescheduleNote(result: RescheduleResult): string {
  if (result.status === 'refused') {
    return `Not rescheduled — ${result.refusal?.detail ?? 'Zocdoc refused the change'}.`
  }
  const moved = `Rescheduled on Zocdoc to ${result.newStartAt ?? 'the new time'}.`
  if (result.behumanMoved) {
    const published = result.slotCreated ? ', and a slot was published for it' : ''
    const settled = result.zocdocConfirmFailure
      ? ` Zocdoc still reads “rescheduled” rather than “confirmed” — ${result.zocdocConfirmFailure}`
      : ''
    return `${moved} The beHuman visit moved with it${published}.${settled}`
  }
  return `${moved} The beHuman visit did NOT move — ${result.behumanFailure ?? 'the calendar write failed'}. Fix it on the practitioner’s calendar; Zocdoc is already on the new time.`
}
